<template>
  <div class="fill-height">
    <line-chart
      v-if="viewMode === 'chart'"
      class="fill-height"
      type="bar"
      :raw-data="players"
      :chart-value-keys="chartValueKeys"
      x-value="last_name"
      :y-value-prefix="aggregation && !removable ? 'latest_stats.' : 'player_stats.'"
      :yaxis="[ { title: { text: $t('chart_legends.distance') }} ]"
      :stacked="true"
      :xaxis="{
        labels: {
          rotate: -45
        },
        categories: players.map(p => p.first_name[0] + '. ' + p.last_name ),
        tickPlacement: 'between'
      }"
      :colors="zoneColors"
      :fill="{ opacity: zoneColors.map(() => 1) }"
    >
      <template v-slot:header>
        <v-card-title class="pa-6 d-flex justify-space-between">
          <div class="subtitle-2 grey--text text--grey-100">
            {{ $t('card_title.distance_zone_breakdown') }}{{ aggregation ? ": " + $t(`variables.${aggregation}.long`) : '' }}
          </div>
          <v-btn
            v-if="removable"
            icon
            style="pointer-events: auto"
            @click="$emit('remove')"
          >
            <v-icon small>
              fal fa-times
            </v-icon>
          </v-btn>
          <visual-list-toggle
            v-else
            :value.sync="viewMode"
          />
        </v-card-title>
      </template>
    </line-chart>

    <v-card
      v-else-if="viewMode === 'table'"
      class="fill-height rounded-lg"
    >
      <v-card-title class="pa-6 d-flex justify-space-between">
        <div class="subtitle-2 grey--text text--grey-100">
          {{ $t('card_title.distance_zone_breakdown') }}{{ aggregation ? ": " + $t(`variables.${aggregation}.long`) : '' }}
        </div>
        <div class="d-flex align-center">
          <visual-list-toggle
            v-if="!removable"
            :value.sync="viewMode"
          />
        </div>
      </v-card-title>

      <chart-list-view-table
        :headers="tableHeaders"
        :items="players"
      >
        <template v-slot:item.last_name="{ item }">
          {{ item.last_name }}, {{ item.first_name }}
        </template>
      </chart-list-view-table>
    </v-card>
  </div>
</template>

<script>
import LineChart from '@/components/charts/LineChart.vue'
import VisualListToggle from '@/components/buttons/VisualListToggle.vue'
import ChartListViewTable from '@/components/charts/implementation/ChartListViewTable.vue'
import { hasSixZoneSetup } from '@/utils/helpers'

const DEFAULT_COLORS = ['#008FFB', '#F4A641', '#F9E03B', '#86CC8E', '#DD644C']
const SIX_ZONE_COLORS = ['#008FFB', '#00E396', '#F9E03B', '#F4A641', '#DD644C', '#775DD0']

export default {
  name: 'SpeedZoneChart',
  components: { ChartListViewTable, LineChart, VisualListToggle },
  props: {
    players: {
      type: Array,
      required: true
    },
    removable: {
      type: Boolean,
      default: false
    },
    aggregation: {
      type: String,
      default: null
    }
  },
  data: () => ({
    viewMode: 'chart'
  }),
  computed: {
    isSixZone() {
      return hasSixZoneSetup()
    },
    zoneColors() {
      return this.isSixZone ? SIX_ZONE_COLORS : DEFAULT_COLORS
    },
    chartEntries() {
      const addAggregation = (key) => this.aggregation && !this.removable ? `${key}_${this.aggregation}` : key

      if (this.isSixZone) {
        return [
          { key: addAggregation('CACTCSVB1TD'), label: this.$t('variables.dist_zone_n', { n: 1 }) },
          { key: addAggregation('CACTCSVB2TD'), label: this.$t('variables.dist_zone_n', { n: 2 }) },
          { key: addAggregation('CACTCSVB3TD'), label: this.$t('variables.dist_zone_n', { n: 3 }) },
          { key: addAggregation('CALETLDLHSR'), label: this.$t('variables.dist_zone_n', { n: 4 }) },
          { key: addAggregation('CALETLDLSP'), label: this.$t('variables.dist_zone_n', { n: 5 }) },
          { key: addAggregation('TDZONE6'), label: this.$t('variables.dist_zone_n', { n: 6 }) }
        ]
      }

      return [
        { key: addAggregation('CACTCSVB1TD'), label: this.$t('variables.CACTCSVB1TD.long') },
        { key: addAggregation('CACTCSVB2TD'), label: this.$t('variables.CACTCSVB2TD.long') },
        { key: addAggregation('CACTCSVB3TD'), label: this.$t('variables.CACTCSVB3TD.long') },
        { key: addAggregation('CALETLDLHSR'), label: this.$t('variables.CALETLDLHSR.long') },
        { key: addAggregation('CALETLDLSP'), label: this.$t('variables.CALETLDLSP.long') }
      ]
    },
    chartValueKeys() {
      return this.chartEntries.map(e => ({ key: e.key, type: 'bar', label: e.label }))
    },
    tableHeaders() {
      const valuePrefix = this.aggregation && !this.removable ? 'latest_stats.' : 'player_stats.'
      return [
        {
          text: this.$t('table_headers.name'),
          value: 'last_name',
          sortable: true,
          width: '20%'
        },
        ...this.chartEntries.map(e => ({
          text: e.label,
          value: valuePrefix + e.key,
          sortable: true,
          align: 'center'
        }))
      ]
    }
  }
}
</script>
